import { Component, Input } from '@angular/core';
import { ViewMode } from '../../../../models';
import {
  CheckoutProductUpgradeExtension,
  CheckoutExtensionUI,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  BadgeValue,
  DEFAULT_BADGE,
} from '@pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/product-upgrade/models';

const PREVIEW_TOKEN_VALUES: Record<string, string> = {
  '{amount-left}': '$4.00',
  '{product_name}': 'Product name',
  '{discount_delta}': '$15.00',
  '{delivery_frequency}': '60 days',
};

function resolveTokens(text: string | undefined | null): string {
  if (!text) return '';
  return Object.entries(PREVIEW_TOKEN_VALUES).reduce((acc, [token, value]) => acc.split(token).join(value), text);
}

const KEEP_INLINE_TAG = /^\/?(?:br|strong|b|em|i|s|strike|del|u|mark)\b/i;

function stripToInline(html: string): string {
  return html.replace(/<([^>]+)>/g, (full, inner) => (KEEP_INLINE_TAG.test(inner) ? full : '')).trim();
}

function unwrapInline(html: string): string {
  if (!html) return '';
  return html
    .replace(/(<\/(?:p|h[1-6]|div)>)\s*(?=<(?:p|h[1-6]|div))/gi, '$1<br>')
    .replace(/<([^>]+)>/g, (full, inner) => (KEEP_INLINE_TAG.test(inner) ? full : ''))
    .trim();
}

export interface RichBlock {
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  html: string;
}

function parseRichText(html: string | undefined | null): RichBlock[] {
  if (!html) return [];
  const resolved = resolveTokens(html);
  const blocks: RichBlock[] = [];
  const re = /<(p|h[1-6])[^>]*>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(resolved)) !== null) {
    const tag = m[1].toLowerCase() as RichBlock['tag'];
    const inner = stripToInline(m[2]);
    if (inner) blocks.push({ tag, html: inner });
  }
  if (blocks.length === 0 && resolved.trim()) {
    blocks.push({ tag: 'p', html: stripToInline(resolved) });
  }
  return blocks;
}

/** Single-line variant for badges — production-side renders badge as SVG which can't break lines. */
function singleLine(html: string): string {
  return unwrapInline(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

@Component({
  selector: 'product-upgrade-example',
  templateUrl: './product-upgrade-example.component.html',
  styleUrls: ['./product-upgrade-example.component.scss'],
  standalone: false,
})
export class ProductUpgradeExampleComponent {
  @Input() template: CheckoutProductUpgradeExtension.Template;
  @Input() mode: ViewMode | null = null;
  @Input() isMobile: boolean = false;

  isUpgradeOpen: boolean = true;

  get displayMode(): CheckoutProductUpgradeExtension.DisplayMode {
    return this.template?.settings?.displayMode ?? CheckoutProductUpgradeExtension.DisplayMode.LINE_ITEM_ACTION;
  }

  get showSubscriptionIndicator(): boolean {
    return !!this.template?.settings?.allowSubscriptions;
  }

  get isLineItemAction(): boolean {
    return this.displayMode === CheckoutProductUpgradeExtension.DisplayMode.LINE_ITEM_ACTION;
  }

  get isStandaloneBlock(): boolean {
    return this.displayMode === CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK;
  }

  get isFocused(): boolean {
    return this.isStandaloneBlock && this.template?.settings?.layoutStyle === 'focused';
  }

  get isSubscriptionPreview(): boolean {
    return (this.template?.productSelection ?? []).some((s) => s?.upgradeProducts?.upgradeType === 'subscription');
  }

  get focusedFrequencyLabel(): string {
    return PREVIEW_TOKEN_VALUES['{delivery_frequency}'];
  }

  get focusedComparePrice(): string {
    return '$12.50';
  }

  get focusedCurrentPrice(): string {
    return '$11.25';
  }

  get titleElements(): RichBlock[] {
    return parseRichText(this.template?.appearance?.titleText?.label ?? 'Upgrade Available');
  }

  get titleTextHtml(): string {
    return unwrapInline(resolveTokens(this.template?.appearance?.titleText?.label ?? 'Upgrade Available'));
  }

  get upgradeTextHtml(): string {
    return unwrapInline(resolveTokens(this.template?.appearance?.upgradeText?.label ?? 'Upgrade'));
  }

  get upgradeTextElements(): RichBlock[] {
    return parseRichText(this.template?.appearance?.upgradeText?.label ?? 'Upgrade');
  }

  get buttonLabelElements(): RichBlock[] {
    const useVariantsLabel = this.template?.settings?.variantSelector === 'outside';
    const label = useVariantsLabel
      ? this.template?.appearance?.button?.labelForProductsWithVariants
      : this.template?.appearance?.button?.label;
    return parseRichText(label ?? 'Upgrade');
  }

  get buttonLabelHtml(): string {
    const useVariantsLabel = this.template?.settings?.variantSelector === 'outside';
    const label = useVariantsLabel
      ? this.template?.appearance?.button?.labelForProductsWithVariants
      : this.template?.appearance?.button?.label;
    return unwrapInline(resolveTokens(label ?? 'Upgrade'));
  }

  get titleAppearanceTone(): string {
    return this.template?.appearance?.titleText?.appearance ?? CheckoutExtensionUI.Appearance.BASE;
  }

  get upgradeTextAppearanceTone(): string {
    return this.template?.appearance?.upgradeText?.appearance ?? CheckoutExtensionUI.Appearance.ACCENT;
  }

  get buttonAppearanceTone(): string {
    return this.template?.appearance?.button?.appearance ?? CheckoutExtensionUI.Appearance.INTERACTIVE;
  }

  get titleBadge(): BadgeValue | null {
    return this.readBadge(this.template?.appearance?.titleText?.badge);
  }

  get upgradeTextBadge(): BadgeValue | null {
    return this.readBadge(this.template?.appearance?.upgradeText?.badge);
  }

  private readBadge(badge: BadgeValue | undefined | null): BadgeValue | null {
    if (!badge?.enabled) return null;
    return {
      enabled: true,
      text: singleLine(resolveTokens(badge.text)) || 'Badge',
      backgroundColor: badge.backgroundColor || DEFAULT_BADGE.backgroundColor,
      textColor: badge.textColor || DEFAULT_BADGE.textColor,
      cornerRadius: badge.cornerRadius || DEFAULT_BADGE.cornerRadius,
      icon: badge.icon || undefined,
    };
  }

  get mainBorder(): string {
    return this.template?.appearance?.mainBackground?.border ?? CheckoutExtensionUI.BorderType.BASE;
  }

  get mainBorderWidth(): string {
    return this.template?.appearance?.mainBackground?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE;
  }

  get mainCornerRadius(): string {
    return this.template?.appearance?.mainBackground?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.LARGE;
  }

  get mainPadding(): string {
    return this.template?.appearance?.mainBackground?.padding ?? CheckoutExtensionUI.Padding.BASE;
  }

  get imageBorder(): string {
    return this.template?.appearance?.image?.border ?? CheckoutExtensionUI.BorderType.NONE;
  }

  get imageBorderWidth(): string {
    return this.template?.appearance?.image?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE;
  }

  get imageCornerRadius(): string {
    return this.template?.appearance?.image?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE;
  }

  get imagePadding(): string {
    return this.template?.appearance?.image?.padding ?? CheckoutExtensionUI.Padding.BASE;
  }

  toggleUpgrade(): void {
    this.isUpgradeOpen = !this.isUpgradeOpen;
  }
}
